"use client";

import { LocaleLink } from "@i18n/routing";
import { Logo } from "@shared/components/Logo";
import { MailIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { FOOTER_STRIP_GROUPS, FOOTER_TOOL_GROUPS } from "../lib/site-nav";
import { EmailLink } from "./EmailLink";

const HEADING_CLASS =
	"mb-3 font-semibold text-gray-12 text-sm uppercase tracking-wide";
const LINK_CLASS = "w-fit text-ink-2 transition-colors hover:text-gray-12";

export function Footer() {
	const t = useTranslations();

	// Blog / Alternatives 以前各占一列只有一条，并进 Company
	const companyLinks = [
		{ label: t("common.menu.pricing"), href: "/pricing" },
		{ label: t("common.menu.blog"), href: "/blog" },
		{ label: "ImagesArt vs VisualGPT", href: "/vs/visualgpt" },
		{
			label: t("common.footer.privacyPolicy"),
			href: "/legal/privacy-policy",
		},
		{ label: t("common.footer.terms"), href: "/legal/terms" },
	];

	return (
		<footer className="relative isolate overflow-hidden bg-gray-4 pt-14 pb-10 font-ui text-sm md:pt-20 md:pb-12">
			{/* DeeVid 页脚底：淡彩光斑 + 白膜（DESIGN.md §7.6.11），纯装饰 */}
			<div
				aria-hidden="true"
				className="-z-10 pointer-events-none absolute inset-0 bg-[radial-gradient(600px_300px_at_20%_0%,rgba(91,0,255,0.10),transparent),radial-gradient(500px_300px_at_80%_20%,rgba(255,0,136,0.08),transparent)]"
			/>
			<div
				aria-hidden="true"
				className="-z-10 pointer-events-none absolute inset-0 bg-white/80"
			/>
			<div className="container">
				{/* 对标 imgveo：品牌一行在上，下面一排等宽列 */}
				<div className="flex items-start justify-between gap-6">
					<div>
						<LocaleLink href="/">
							<Logo />
						</LocaleLink>
						<p className="mt-3 max-w-md text-ink-2 leading-6">
							{t("common.footer.brandDescription")}
						</p>
					</div>
					<EmailLink className="flex size-10 shrink-0 items-center justify-center rounded-full border border-black/10 text-ink-2 transition-colors hover:text-gray-12">
						<MailIcon className="size-4" />
						<span className="sr-only">
							{t("common.footer.contactUs")}
						</span>
					</EmailLink>
				</div>

				<div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-10 leading-6 md:grid-cols-4">
					{FOOTER_TOOL_GROUPS.map((group) => (
						<nav
							key={group.labelKey}
							aria-label={t(group.labelKey)}
							className="flex flex-col gap-2.5"
						>
							<div className={HEADING_CLASS}>
								{t(group.labelKey)}
							</div>
							{group.items.map((item) => (
								<LocaleLink
									key={item.href}
									href={item.href}
									className={LINK_CLASS}
								>
									{t(item.titleKey)}
								</LocaleLink>
							))}
						</nav>
					))}

					<nav
						aria-label={t("common.menu.company")}
						className="flex flex-col gap-2.5"
					>
						<div className={HEADING_CLASS}>
							{t("common.menu.company")}
						</div>
						{companyLinks.map((link) => (
							<LocaleLink
								key={link.href}
								href={link.href}
								className={LINK_CLASS}
							>
								{link.label}
							</LocaleLink>
						))}
						<EmailLink className={LINK_CLASS}>
							{t("common.footer.contactUs")}
						</EmailLink>
					</nav>
				</div>

				{/* 工具和特效：小号灰字横排成标签条，每组占满一行；链接全留（全站内链，首页只靠它链到这些页，10/29 老公定） */}
				<div className="mt-10 flex flex-col gap-8">
					{FOOTER_STRIP_GROUPS.map((group) => (
						<nav
							key={group.labelKey}
							aria-label={t(group.labelKey)}
						>
							<div className={HEADING_CLASS}>
								{t(group.labelKey)}
							</div>
							<ul className="flex flex-wrap gap-y-1 text-ink-3 text-xs leading-5">
								{group.items.map((item) => (
									<li
										key={item.href}
										className="after:mx-2 after:content-['·'] last:after:content-none"
									>
										<LocaleLink
											href={item.href}
											className="transition-colors hover:text-gray-12"
										>
											{t(item.titleKey)}
										</LocaleLink>
									</li>
								))}
							</ul>
						</nav>
					))}
				</div>

				<div className="mt-12 flex flex-col items-start justify-between gap-4 border-black/[.08] border-t pt-6 sm:flex-row sm:items-center">
					<p className="text-ink-3 text-xs">
						© {new Date().getFullYear()} ImagesArt AI.{" "}
						{t("common.footer.allRightsReserved")}
					</p>
				</div>
			</div>
		</footer>
	);
}
